// wizardConstants.ts - Constants and default values for ProductCreationWizard
import type { ProductData } from "../components/ProductCreationWizard/ProductCreationWizard"
import type { 
  BasicInfoData,
  SalesInfoData,
  ClassificationData,
  ShippingData,
  ReviewData
} from "../components/WizardSteps"

// Wizard step definitions
export const WIZARD_STEPS = [
  { id: "basic", label: "Basic Info", shortLabel: "Info" },
  { id: "classification", label: "Category", shortLabel: "Category" },
  { id: "sales", label: "Sales Info", shortLabel: "Sales" },
  { id: "shipping", label: "Shipping", shortLabel: "Ship" },
  { id: "review", label: "Review", shortLabel: "Review" }
]

export const TOTAL_STEPS = 5

// Default empty form data structure
export const EMPTY_FORM_DATA: ProductData = {
  basicInfo: {} as BasicInfoData,
  classification: {} as ClassificationData,
  salesInfo: {
    minOrder: 1,
    hasVariants: false,
    variantOptions: [],
    variantCombinations: [],
    warehouseStock: {}
  } as SalesInfoData,
  shipping: {} as ShippingData,
  review: {} as ReviewData
}

// Create default form data with optional initial data
export function createDefaultFormData(initialData?: Partial<ProductData>, mode: 'create' | 'edit' = 'create'): ProductData {
  if (mode === 'edit' && initialData && Object.keys(initialData).length > 0) {
    return {
      basicInfo: {
        productSku: '',
        productName: '',
        productDescription: '',
        productImages: [],
        videoUrl: '',
        ...initialData.basicInfo
      },
      classification: {
        brandId: '',
        brandName: '',
        categoryPath: [],
        categoryAttributes: {},
        licenseFile: null,
        ...initialData.classification
      },
      salesInfo: {
        minOrder: 1,
        hasVariants: false,
        variantOptions: [],
        variantCombinations: [],
        warehouseStock: {},
        productPrice: 0,
        costPrice: 0,
        currency: 'IDR',
        totalStock: 0,
        tags: [],
        promotionTags: [],
        ...initialData.salesInfo
      },
      shipping: {
        packageDimensions: {
          height: 0,
          length: 0,
          width: 0,
          weight: 0
        },
        isDangerous: false,
        packingInstructions: '',
        shippingCompanies: [],
        preOrderSettings: {
          enabled: false,
          type: 'PRE_ORDER',
          processingDays: 7
        },
        ...initialData.shipping
      },
      review: {
        publishImmediately: true,
        ...initialData.review
      }
    }
  }
  
  // For create mode or empty initial data
  return {
    basicInfo: initialData?.basicInfo || ({} as BasicInfoData),
    classification: initialData?.classification || ({} as ClassificationData),
    salesInfo: {
      minOrder: 1,
      hasVariants: false,
      variantOptions: [],
      variantCombinations: [],
      warehouseStock: {},
      ...initialData?.salesInfo
    } as SalesInfoData,
    shipping: initialData?.shipping || ({} as ShippingData),
    review: initialData?.review || ({} as ReviewData)
  }
}

// Persistence configuration
export const PERSISTENCE_CONFIG = {
  STALE_DATA_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours
  RELOAD_FLAG_SUFFIX: "_reload_flag",
  DEFAULT_KEY: "productWizard"
}